/**
 * Mobile Device Detection Utility
 *
 * Simple mobile device detection based on user agent.
 */

/**
 * Check if current device is mobile based on user agent
 */
export const isMobileUserAgent = (userAgent?: string): boolean => {
    const ua = userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : '');
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
};

/**
 * Simple mobile detection function - uses user agent detection only
 *
 * @returns True if device is detected as mobile
 */
export const isMobileDevice = (): boolean => {
    return isMobileUserAgent();
};

/**
 * Detect if current device is iOS based on user agent
 */
export const isIOSDevice = (userAgent?: string): boolean => {
    const ua = userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : '');
    return /iPhone|iPad|iPod/i.test(ua);
};

/**
 * Detect if current device is Android based on user agent
 */
export const isAndroidDevice = (userAgent?: string): boolean => {
    const ua = userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : '');
    return /Android/i.test(ua);
};

/**
 * Device type classification
 */
export type DeviceType = 'mobile' | 'tablet' | 'desktop';

/**
 * Detect device type based on user agent
 *
 * @param userAgent - Optional user agent string. If not provided, uses navigator.userAgent
 * @returns 'mobile', 'tablet', or 'desktop'
 */
export const getDeviceType = (userAgent?: string, maxTouchPoints?: number): DeviceType => {
    const ua = userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : '');
    const touchPoints =
        maxTouchPoints ?? (typeof navigator !== 'undefined' ? navigator.maxTouchPoints : 0);

    // Check for tablet first (iPad, iPadOS desktop UA, Android tablets)
    if (
        /iPad/i.test(ua) ||
        (/Macintosh/i.test(ua) && touchPoints > 1) ||
        (/Android/i.test(ua) && !/Mobile/i.test(ua))
    ) {
        return 'tablet';
    }

    // Check for mobile devices
    if (/Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
        return 'mobile';
    }

    // Default to desktop
    return 'desktop';
};
